import { useEffect, useMemo, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { AssetBulkPatch, AssetBulkStatus, AssetCollectionRecord, AssetKind, AssetRecord } from '@shared/types';
import { inferAssetKind } from '@shared/utils/assets';
import { dispatchShellDomainInvalidated, subscribeToShellDomainInvalidations } from '@renderer/app/events/shell-domain-events';
import { InlineQuickAddPanel, createQuickAddContext } from '@renderer/app/quick-add/quick-add-shared';
import { useShellStore } from '@renderer/app/state/shell-store';
import { Icon, type IconName } from '@renderer/shared/components/icons';
import { BulkEditorRail } from '@renderer/shared/components/bulk-editor-rail';
import {
  CollectionSelectionToggle,
  CollectionViewToggle,
  DataTableSurface,
  EmptyCollectionState
} from '@renderer/shared/components/collection-scaffold';
import {
  CollectionColumnSettingsModal,
  resolveVisibleCollectionColumns,
  type CollectionColumnConfig
} from '@renderer/shared/components/collection-column-settings';
import { CollectionTable, type CollectionSortState } from '@renderer/shared/components/collection-table';
import { SplitWorkspaceLayout } from '@renderer/shared/components/split-workspace';
import { IconButton, OverflowMenuButton, PanelCard, SectionHeader, SelectField, TextField } from '@renderer/shared/components/primitives';
import { useI18n } from '@renderer/shared/i18n';
import { getAssetPreviewUrl } from '@renderer/shared/utils/asset-url';
import loaderIcon from '@renderer/assets/icons/animated/loader.svg';
import { applyBulkAssetPatch, deleteAssets, importAssets, listAssets } from './resources-repository';
import { useResourcesViewStore, type ResourcesCategoryFilter, type ResourcesSort } from './resources-view-store';
import { ResourceCard } from './components/resource-card';
import { ResourcesToolbar } from './components/resources-toolbar';
import { AssetDetailsModal } from './components/asset-details-modal';

type PatchMode = 'unchanged' | 'clear' | 'set';
type ResourceBulkDraft = Omit<AssetBulkPatch, 'assetIds'>;

const assetKinds: AssetKind[] = ['logo', 'icon', 'document', 'image', 'pdf'];

function createInitialResourceBulkDraft(): ResourceBulkDraft {
  return {
    status: { mode: 'unchanged' },
    kind: { mode: 'unchanged' },
    projectId: { mode: 'unchanged' },
    customerId: { mode: 'unchanged' }
  };
}

function resourceSortToState(sort: ResourcesSort): CollectionSortState {
  switch (sort) {
    case 'oldest':
      return { columnId: 'updatedAt', direction: 'asc' };
    case 'name_asc':
      return { columnId: 'name', direction: 'asc' };
    case 'name_desc':
      return { columnId: 'name', direction: 'desc' };
    default:
      return { columnId: 'updatedAt', direction: 'desc' };
  }
}

function resourceTableSortToSort(sortState: CollectionSortState): ResourcesSort | null {
  if (sortState.columnId === 'updatedAt') {
    return sortState.direction === 'asc' ? 'oldest' : 'recent';
  }

  if (sortState.columnId === 'name') {
    return sortState.direction === 'asc' ? 'name_asc' : 'name_desc';
  }

  return null;
}

function toAssetPreviewUrl(asset: AssetRecord): string | null {
  if (!asset.storagePath) {
    return null;
  }

  if (asset.kind !== 'image' && asset.kind !== 'icon' && asset.kind !== 'logo') {
    return null;
  }

  return getAssetPreviewUrl(asset.id);
}

function getAssetKindIcon(kind: AssetKind): IconName {
  switch (kind) {
    case 'image':
      return 'images';
    case 'pdf':
    case 'document':
      return 'document';
    case 'logo':
    case 'icon':
      return 'icon';
    default:
      return 'document';
  }
}

function getCategoryKinds(category: 'all' | 'documents' | 'images' | 'icons'): AssetKind[] {
  switch (category) {
    case 'documents':
      return ['document', 'pdf'];
    case 'images':
      return ['image'];
    case 'icons':
      return ['icon', 'logo'];
    default:
      return ['logo', 'icon', 'document', 'image', 'pdf'];
  }
}

export function ResourcesScreen(): JSX.Element {
  const { locale, t } = useI18n();
  const preferences = useShellStore((state) => state.preferences);
  const savePreferences = useShellStore((state) => state.savePreferences);
  const [searchParams] = useSearchParams();
  const [collection, setCollection] = useState<AssetCollectionRecord | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [resourceBulkDraft, setResourceBulkDraft] = useState<ResourceBulkDraft>(createInitialResourceBulkDraft);
  const [columnSettingsOpen, setColumnSettingsOpen] = useState(false);
  const [pendingImports, setPendingImports] = useState<Array<{ id: string; name: string; kind: AssetKind }>>([]);
  const dragDepthRef = useRef(0);
  const [isDropActive, setIsDropActive] = useState(false);
  const [activeAssetId, setActiveAssetId] = useState<string | null>(null);
  const { categoryFilter, projectFilter, sort, query, setCategoryFilter, setProjectFilter, setSort, setQuery, setViewMode } =
    useResourcesViewStore();

  useEffect(() => {
    let active = true;
    setError(null);

    void listAssets()
      .then((nextCollection) => {
        if (active) {
          setCollection(nextCollection);
        }
      })
      .catch((nextError) => {
        if (active) {
          setError(nextError instanceof Error ? nextError.message : 'Resources unavailable.');
        }
      });

    return () => {
      active = false;
    };
  }, [reloadToken]);

  useEffect(() => {
    if (!collection) {
      return;
    }

    const projectId = searchParams.get('projectId');
    if (!projectId) {
      return;
    }

    if (projectFilter !== 'all') {
      return;
    }

    if (!collection.projectSummaries.some((project) => project.id === projectId)) {
      return;
    }

    setProjectFilter(projectId);
  }, [collection, projectFilter, searchParams, setProjectFilter]);

  useEffect(
    () =>
      subscribeToShellDomainInvalidations((detail) => {
        if (detail.domain === 'resources') {
          setReloadToken((value) => value + 1);
        }
      }),
    []
  );

  const subtitle = useMemo(() => {
    if (!collection) {
      return '';
    }

    const total = collection.assets.length;
    const kinds = new Set(collection.assets.map((asset) => asset.kind));
    return t('resources.summary.subtitle', { total, categories: kinds.size });
  }, [collection, t]);

  const projectOptions = useMemo(() => {
    if (!collection) {
      return [{ value: 'all', label: t('resources.filters.allProjects') }];
    }

    return [
      { value: 'all', label: t('resources.filters.allProjects') },
      ...[...collection.projectSummaries].sort((a, b) => a.name.localeCompare(b.name)).map((project) => ({ value: project.id, label: project.name }))
    ];
  }, [collection, t]);

  const categoryTabs = useMemo<Array<{ id: ResourcesCategoryFilter; label: string; icon: IconName }>>(
    () => [
      { id: 'all', label: t('resources.categories.all'), icon: 'all' },
      { id: 'documents', label: t('resources.categories.documents'), icon: 'document' },
      { id: 'images', label: t('resources.categories.images'), icon: 'images' },
      { id: 'icons', label: t('resources.categories.icons'), icon: 'icon' }
    ],
    [t]
  );

  const sortOptions = useMemo<Array<{ value: ResourcesSort; label: string }>>(
    () => [
      { value: 'recent', label: t('resources.sort.recent') },
      { value: 'oldest', label: t('resources.sort.oldest') },
      { value: 'name_asc', label: t('resources.sort.nameAsc') },
      { value: 'name_desc', label: t('resources.sort.nameDesc') }
    ],
    [t]
  );

  const formatter = useMemo(() => new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'short', year: 'numeric' }), [locale]);
  const resourceColumns = useMemo<CollectionColumnConfig[]>(
    () => [
      { id: 'selection', label: '', className: 'av-collection-table__selection-cell', locked: true },
      { id: 'name', label: t('resources.table.name'), sortable: true, locked: true },
      { id: 'kind', label: t('resources.table.kind') },
      { id: 'project', label: t('resources.table.project') },
      { id: 'customer', label: t('resources.table.customer') },
      { id: 'updatedAt', label: t('resources.table.updatedAt'), sortable: true },
      { id: 'status', label: t('resources.table.status') },
      { id: 'actions', label: '', align: 'right', locked: true }
    ],
    [t]
  );
  const visibleResourceColumns = useMemo(
    () => resolveVisibleCollectionColumns(resourceColumns, preferences.collectionColumnVisibility.resources),
    [preferences.collectionColumnVisibility.resources, resourceColumns]
  );
  const customerOptions = useMemo(() => {
    if (!collection) {
      return [];
    }

    return [...collection.customerSummaries].sort((a, b) => a.name.localeCompare(b.name)).map((customer) => ({
      value: customer.id,
      label: customer.name
    }));
  }, [collection]);
  const projectNameById = useMemo(
    () => new Map((collection?.projectSummaries ?? []).map((project) => [project.id, project.name] as const)),
    [collection]
  );
  const customerNameById = useMemo(
    () => new Map((collection?.customerSummaries ?? []).map((customer) => [customer.id, customer.name] as const)),
    [collection]
  );
  const quickAddContext = createQuickAddContext({
    customers: collection?.customerSummaries ?? [],
    projects: collection?.projectSummaries ?? [],
    t
  });

  const visibleAssets = useMemo(() => {
    if (!collection) {
      return [];
    }

    const allowedKinds = new Set(getCategoryKinds(categoryFilter));
    const lowerQuery = query.trim().toLowerCase();

    const filtered = collection.assets.filter((asset) => {
      if (!allowedKinds.has(asset.kind)) {
        return false;
      }

      if (projectFilter !== 'all' && asset.projectId !== projectFilter) {
        return false;
      }

      if (lowerQuery && !asset.name.toLowerCase().includes(lowerQuery)) {
        return false;
      }

      return true;
    });

    const sorted = [...filtered].sort((left, right) => {
      switch (sort) {
        case 'oldest':
          return new Date(left.updatedAt).getTime() - new Date(right.updatedAt).getTime();
        case 'name_asc':
          return left.name.localeCompare(right.name);
        case 'name_desc':
          return right.name.localeCompare(left.name);
        case 'recent':
        default:
          return new Date(right.updatedAt).getTime() - new Date(left.updatedAt).getTime();
      }
    });

    return sorted;
  }, [categoryFilter, collection, projectFilter, query, sort]);

  const selectedAssets = useMemo(() => {
    if (!collection) {
      return [];
    }

    const selectedSet = new Set(selectedIds);
    return collection.assets.filter((asset) => selectedSet.has(asset.id));
  }, [collection, selectedIds]);

  const hiddenSelectedCount = useMemo(() => {
    const visibleSet = new Set(visibleAssets.map((asset) => asset.id));
    return selectedIds.filter((id) => !visibleSet.has(id)).length;
  }, [selectedIds, visibleAssets]);

  if (error) {
    return (
      <div className="av-page">
        <EmptyCollectionState title={t('navigation.resources')} description={error} />
      </div>
    );
  }

  if (!collection) {
    return <div className="av-page" />;
  }

  const hasResourceBulkDraftChanges = Object.values(resourceBulkDraft).some((patch) => patch.mode !== 'unchanged');
  const isResourceBulkDraftValid =
    (resourceBulkDraft.projectId.mode !== 'set' || resourceBulkDraft.projectId.value.length > 0) &&
    (resourceBulkDraft.customerId.mode !== 'set' || resourceBulkDraft.customerId.value.length > 0);
  const tableSortState = resourceSortToState(sort);

  function updateResourceBulkDraft(patch: Partial<ResourceBulkDraft>): void {
    setResourceBulkDraft((current) => ({
      ...current,
      ...patch
    }));
  }

  function handleTableSortChange(nextSortState: CollectionSortState): void {
    const nextSort = resourceTableSortToSort(nextSortState);

    if (nextSort) {
      setSort(nextSort);
    }
  }

  function handleVisibleColumnIdsChange(columnIds: string[]): void {
    void savePreferences({
      collectionColumnVisibility: {
        ...preferences.collectionColumnVisibility,
        resources: columnIds
      }
    });
  }

  function handleResetVisibleColumns(): void {
    const nextVisibility = { ...preferences.collectionColumnVisibility };
    delete nextVisibility.resources;
    void savePreferences({ collectionColumnVisibility: nextVisibility });
  }

  function getProjectLabel(projectId: string | null): string {
    return projectId ? projectNameById.get(projectId) ?? t('common.notSet') : t('common.notSet');
  }

  function getCustomerLabel(customerId: string | null): string {
    return customerId ? customerNameById.get(customerId) ?? t('common.notSet') : t('common.notSet');
  }

  function renderAssetCell(asset: AssetRecord, columnId: string): JSX.Element {
    const selected = selectedIds.includes(asset.id);
    const status = asset.archivedAt ? 'archived' : 'active';

    switch (columnId) {
      case 'selection':
        return (
          <td key={columnId} className="av-collection-table__cell av-collection-table__selection-cell">
            <CollectionSelectionToggle
              selected={selected}
              icon={getAssetKindIcon(asset.kind)}
              imageSrc={toAssetPreviewUrl(asset)}
              label={asset.name}
              onClick={() => toggleSelected(asset.id)}
            />
          </td>
        );
      case 'name':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <div className="av-collection-table__identity-copy">
              <strong>{asset.name}</strong>
              <span>{asset.mimeType ?? t('common.notSet')}</span>
            </div>
          </td>
        );
      case 'kind':
        return (
          <td key={columnId} className="av-collection-table__cell">
            <span className="av-type-pill">{t(`enums.assetKind.${asset.kind}`)}</span>
          </td>
        );
      case 'project':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {getProjectLabel(asset.projectId)}
          </td>
        );
      case 'customer':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {getCustomerLabel(asset.customerId)}
          </td>
        );
      case 'updatedAt':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {formatter.format(new Date(asset.updatedAt))}
          </td>
        );
      case 'status':
        return (
          <td key={columnId} className="av-collection-table__cell">
            {t(`resources.status.${status}`)}
          </td>
        );
      case 'actions':
        return (
          <td key={columnId} className="av-collection-table__cell is-right">
            <OverflowMenuButton onClick={() => setActiveAssetId(asset.id)} />
          </td>
        );
      default:
        return <td key={columnId} className="av-collection-table__cell" />;
    }
  }

  async function handleImportFiles(files: File[]): Promise<void> {
    if (files.length === 0) {
      return;
    }

    setMutationError(null);

    const pending = files.map((file) => ({
      id: typeof crypto?.randomUUID === 'function' ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`,
      name: file.name,
      kind: inferAssetKind(file.name, file.type || null)
    }));

    setPendingImports(pending);

    try {
      const items = await Promise.all(
        files.map(async (file) => {
          const fileWithPath = file as File & { path?: string };
          const sourcePath = typeof fileWithPath.path === 'string' ? fileWithPath.path : null;

          if (sourcePath) {
            return {
              fileName: file.name,
              mimeType: file.type || null,
              sourcePath
            };
          }

          const buffer = await file.arrayBuffer();
          return {
            fileName: file.name,
            mimeType: file.type || null,
            bytes: new Uint8Array(buffer)
          };
        })
      );

      const nextCollection = await importAssets({
        items,
        projectId: projectFilter === 'all' ? null : projectFilter,
        customerId: null
      });

      setCollection(nextCollection);
      dispatchShellDomainInvalidated('resources');
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Upload failed.');
    } finally {
      setPendingImports([]);
    }
  }

  function toggleSelected(assetId: string): void {
    setSelectedIds((current) => (current.includes(assetId) ? current.filter((id) => id !== assetId) : [...current, assetId]));
  }

  function clearSelection(): void {
    setSelectedIds([]);
    setResourceBulkDraft(createInitialResourceBulkDraft());
  }

  async function handleDeleteSelected(): Promise<void> {
    if (selectedIds.length === 0) {
      return;
    }

    if (!window.confirm(t('common.confirmDeleteSoft'))) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await deleteAssets({ ids: selectedIds });
      setCollection(nextCollection);
      clearSelection();
      dispatchShellDomainInvalidated('resources');
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Delete failed.');
    }
  }

  async function handleApplyBulkChanges(): Promise<void> {
    if (selectedIds.length === 0 || !hasResourceBulkDraftChanges || !isResourceBulkDraftValid) {
      return;
    }

    setMutationError(null);

    try {
      const nextCollection = await applyBulkAssetPatch({
        assetIds: selectedIds,
        ...resourceBulkDraft
      });

      setCollection(nextCollection);
      dispatchShellDomainInvalidated('resources');
      clearSelection();
    } catch (nextError) {
      setMutationError(nextError instanceof Error ? nextError.message : 'Update failed.');
    }
  }

  async function handleUpload(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const files = event.target.files ? Array.from(event.target.files) : [];
    event.target.value = '';

    await handleImportFiles(files);
  }

  return (
    <div className="av-page">
      <SectionHeader
        title={t('navigation.resources')}
        subtitle={subtitle}
        actions={
          <div className="av-page-search">
            <Icon name="search" />
            <TextField
              className="av-page-search__input"
              unstyled
              value={query}
              onValueChange={setQuery}
              placeholder={t('resources.search.placeholder')}
            />
          </div>
        }
      />

      <InlineQuickAddPanel domain="resources" context={quickAddContext} onCreated={async () => setCollection(await listAssets())} />

      <ResourcesToolbar
        projectOptions={projectOptions}
        projectFilter={projectFilter}
        onProjectFilterChange={setProjectFilter}
        sort={sort}
        sortOptions={sortOptions}
        onSortChange={setSort}
        categoryFilter={categoryFilter}
        onCategoryFilterChange={setCategoryFilter}
        categoryTabs={categoryTabs}
        uploadAction={
          <>
            <input ref={fileInputRef} className="av-file-input" type="file" multiple onChange={(event) => void handleUpload(event)} />
            <button className="av-secondary-button" type="button" onClick={() => fileInputRef.current?.click()}>
              <Icon name="plus" />
              <span>{t('resources.actions.upload')}</span>
            </button>
          </>
        }
        trailing={
          <>
            <CollectionViewToggle
              options={[
                { value: 'list', label: t('common.view.list'), icon: 'list' },
                { value: 'grid', label: t('common.view.grid'), icon: 'grid' }
              ]}
              value={preferences.resourcesViewMode}
              onChange={(value) => void setViewMode(value)}
            />
            <IconButton
              icon="settings"
              label={t('common.columns.settingsButton')}
              onClick={() => setColumnSettingsOpen(true)}
            />
          </>
        }
      />

      {mutationError ? <div className="av-inline-error">{mutationError}</div> : null}

      <SplitWorkspaceLayout
        railMode="fixed"
        main={
          preferences.resourcesViewMode === 'list' ? (
            visibleAssets.length === 0 ? (
              <EmptyCollectionState title={t('navigation.resources')} description={t('common.noResults')} />
            ) : (
              <DataTableSurface>
                <CollectionTable
                  ariaLabel={t('navigation.resources')}
                  columns={visibleResourceColumns}
                  sortState={tableSortState}
                  onSortChange={handleTableSortChange}
                >
                  {visibleAssets.map((asset) => (
                    <tr
                      key={asset.id}
                      className={['av-collection-table__row', selectedIds.includes(asset.id) ? 'is-selected' : ''].filter(Boolean).join(' ')}
                    >
                      {visibleResourceColumns.map((column) => renderAssetCell(asset, column.id))}
                    </tr>
                  ))}
                </CollectionTable>
              </DataTableSurface>
            )
          ) : (
            <PanelCard
            className="av-resource-surface"
            onDragEnter={(event) => {
              event.preventDefault();
              dragDepthRef.current += 1;
              setIsDropActive(true);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              if (event.dataTransfer) {
                event.dataTransfer.dropEffect = 'copy';
              }
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
              if (dragDepthRef.current === 0) {
                setIsDropActive(false);
              }
            }}
            onDrop={(event) => {
              event.preventDefault();
              dragDepthRef.current = 0;
              setIsDropActive(false);
              void handleImportFiles(Array.from(event.dataTransfer.files ?? []));
            }}
          >
            {isDropActive ? (
              <div className="av-resource-drop-overlay" aria-hidden>
                <strong>{t('resources.upload.dropTitle')}</strong>
                <span>{t('resources.upload.dropSubtitle')}</span>
              </div>
            ) : null}

            {visibleAssets.length === 0 && pendingImports.length === 0 ? (
              <EmptyCollectionState title={t('navigation.resources')} description={t('common.noResults')} />
            ) : (
              <div className="av-resource-grid">
                {pendingImports.map((pending) => (
                  <article key={pending.id} className="av-resource-card is-pending">
                    <div className="av-resource-card__preview">
                      <div
                        className={[
                          'av-resource-card__preview-fallback',
                          pending.kind === 'pdf' ? 'is-placeholder-warm' : '',
                          pending.kind === 'icon' || pending.kind === 'logo' ? 'is-placeholder-blue' : '',
                          pending.kind === 'image' ? 'is-placeholder-cool' : ''
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        aria-hidden
                      >
                        <Icon name={getAssetKindIcon(pending.kind)} />
                      </div>
                      <div className="av-resource-card__loader" aria-hidden>
                        <img alt="" src={loaderIcon} />
                      </div>
                    </div>
                    <div className="av-resource-card__body">
                      <div className="av-resource-card__title-row">
                        <span className="av-resource-card__title">{pending.name}</span>
                        <span className="av-type-pill">{t(`enums.assetKind.${pending.kind}`)}</span>
                      </div>
                      <div className="av-resource-card__meta">
                        <Icon name="timer" />
                        <span>{t('resources.upload.importing')}</span>
                      </div>
                    </div>
                  </article>
                ))}

                {visibleAssets.map((asset) => (
                  <ResourceCard
                    key={asset.id}
                    id={asset.id}
                    name={asset.name}
                    kind={asset.kind}
                    kindLabel={t(`enums.assetKind.${asset.kind}`)}
                    metaLabel={formatter.format(new Date(asset.updatedAt))}
                    previewUrl={toAssetPreviewUrl(asset)}
                    selected={selectedIds.includes(asset.id)}
                    onSelect={toggleSelected}
                    onOpenDetails={(id) => setActiveAssetId(id)}
                  />
                ))}
              </div>
            )}
            </PanelCard>
          )
        }
        rail={
          selectedAssets.length > 0 ? (
            <BulkEditorRail
              title={t('resources.selection.title', { count: selectedAssets.length })}
              items={selectedAssets.map((asset) => ({
                id: asset.id,
                icon: 'resources',
                label: asset.name,
                subtitle: t(`enums.assetKind.${asset.kind}`)
              }))}
              hiddenSelectedCount={hiddenSelectedCount}
              onClose={clearSelection}
              onRemoveItem={(id) => setSelectedIds((current) => current.filter((entry) => entry !== id))}
              destructiveAction={{ label: t('shell.delete'), onClick: () => void handleDeleteSelected() }}
              primaryAction={{
                label: t('common.applyChanges'),
                disabled: !hasResourceBulkDraftChanges || !isResourceBulkDraftValid,
                onClick: () => void handleApplyBulkChanges()
              }}
            >
              <div className="av-bulk-editor-form">
                <label className="av-form-field">
                  <span>{t('resources.selection.statusLabel')}</span>
                  <SelectField
                    value={resourceBulkDraft.status.mode === 'set' ? resourceBulkDraft.status.value : 'unchanged'}
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'active', label: t('resources.status.active') },
                      { value: 'archived', label: t('resources.status.archived') }
                    ]}
                    onChange={(event) =>
                      updateResourceBulkDraft({
                        status:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as AssetBulkStatus }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('resources.table.kind')}</span>
                  <SelectField
                    value={resourceBulkDraft.kind.mode === 'set' ? resourceBulkDraft.kind.value : 'unchanged'}
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      ...assetKinds.map((kind) => ({
                        value: kind,
                        label: t(`enums.assetKind.${kind}`)
                      }))
                    ]}
                    onChange={(event) =>
                      updateResourceBulkDraft({
                        kind:
                          event.target.value === 'unchanged'
                            ? { mode: 'unchanged' }
                            : { mode: 'set', value: event.target.value as AssetKind }
                      })
                    }
                  />
                </label>

                <label className="av-form-field">
                  <span>{t('resources.table.project')}</span>
                  <SelectField
                    value={resourceBulkDraft.projectId.mode}
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    onChange={(event) => {
                      const mode = event.target.value as PatchMode;
                      updateResourceBulkDraft({
                        projectId:
                          mode === 'set'
                            ? { mode: 'set', value: projectOptions.find((option) => option.value !== 'all')?.value ?? '' }
                            : { mode }
                      });
                    }}
                  />
                  {resourceBulkDraft.projectId.mode === 'set' ? (
                    <SelectField
                      value={resourceBulkDraft.projectId.value}
                      options={projectOptions.filter((option) => option.value !== 'all')}
                      onChange={(event) => updateResourceBulkDraft({ projectId: { mode: 'set', value: event.target.value } })}
                    />
                  ) : null}
                </label>

                <label className="av-form-field">
                  <span>{t('resources.table.customer')}</span>
                  <SelectField
                    value={resourceBulkDraft.customerId.mode}
                    options={[
                      { value: 'unchanged', label: t('common.leaveUnchanged') },
                      { value: 'clear', label: t('common.clearValue') },
                      { value: 'set', label: t('common.setValue') }
                    ]}
                    onChange={(event) => {
                      const mode = event.target.value as PatchMode;
                      updateResourceBulkDraft({
                        customerId:
                          mode === 'set'
                            ? { mode: 'set', value: customerOptions[0]?.value ?? '' }
                            : { mode }
                      });
                    }}
                  />
                  {resourceBulkDraft.customerId.mode === 'set' ? (
                    <SelectField
                      value={resourceBulkDraft.customerId.value}
                      options={customerOptions}
                      onChange={(event) => updateResourceBulkDraft({ customerId: { mode: 'set', value: event.target.value } })}
                    />
                  ) : null}
                </label>
              </div>
            </BulkEditorRail>
          ) : null
        }
      />

      <AssetDetailsModal
        assetId={activeAssetId}
        isOpen={Boolean(activeAssetId)}
        onClose={() => setActiveAssetId(null)}
        onSaved={(detail) => {
          setCollection((current) => {
            if (!current) {
              return current;
            }

            return {
              ...current,
              assets: current.assets.map((asset) => (asset.id === detail.asset.id ? detail.asset : asset))
            };
          });
          dispatchShellDomainInvalidated('resources');
        }}
      />

      <CollectionColumnSettingsModal
        columns={resourceColumns}
        domain="resources"
        isOpen={columnSettingsOpen}
        strings={{
          title: t('common.columns.settingsTitle'),
          description: t('common.columns.settingsDescription'),
          reset: t('common.columns.settingsReset'),
          close: t('common.close'),
          locked: t('common.columns.settingsLocked')
        }}
        visibleColumnIds={preferences.collectionColumnVisibility.resources}
        onClose={() => setColumnSettingsOpen(false)}
        onReset={handleResetVisibleColumns}
        onVisibleColumnIdsChange={handleVisibleColumnIdsChange}
      />
    </div>
  );
}
